import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  SwapHoriz as SwapHorizIcon,
  LocalShipping as LocalShippingIcon
} from '@mui/icons-material';
import apiService from '../../services/apiService';

const BalanceMovementList = () => {
  const [movements, setMovements] = useState([]);
  const [filteredMovements, setFilteredMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('all');
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  // Definir applyFilters con useCallback antes de useEffect
  const applyFilters = useCallback(() => {
    let filtered = [...movements];
    
    // Filtrar por término de búsqueda
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        movement =>
          movement.branch?.branch_name?.toLowerCase().includes(lowercasedSearch) ||
          movement.comments?.toLowerCase().includes(lowercasedSearch) ||
          (movement.user && 
            (`${movement.user.first_name} ${movement.user.father_last_name}`).toLowerCase().includes(lowercasedSearch))
      );
    }
    
    // Filtrar por sucursal
    if (branchFilter !== 'all') {
      filtered = filtered.filter(movement => movement.branch.id === parseInt(branchFilter));
    }
    
    // Filtrar por moneda
    if (currencyFilter !== 'all') {
      filtered = filtered.filter(movement => movement.currency_type === currencyFilter);
    }
    
    // Filtrar por fecha
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      switch (dateFilter) {
        case 'today':
          filtered = filtered.filter(movement => {
            const movementDate = new Date(movement.movement_date);
            return movementDate >= today;
          });
          break;
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          filtered = filtered.filter(movement => {
            const movementDate = new Date(movement.movement_date);
            return movementDate >= weekAgo;
          });
          break;
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          filtered = filtered.filter(movement => {
            const movementDate = new Date(movement.movement_date);
            return movementDate >= monthAgo;
          });
          break;
        default:
          break;
      }
    }
    
    setFilteredMovements(filtered);
    setPage(0);
  }, [searchTerm, branchFilter, currencyFilter, dateFilter, movements]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Cargar sucursales para filtros
        const branchesData = await apiService.getBranches();
        setBranches(branchesData);
        
        // Cargar movimientos de balance
        const movementsData = await apiService.getBalanceMovements();
        setMovements(movementsData);
        setFilteredMovements(movementsData);
      } catch (err) {
        console.error('Error al cargar movimientos de balance:', err);
        setError('Error al cargar los movimientos de balance. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const clearFilters = () => {
    setBranchFilter('all');
    setCurrencyFilter('all');
    setDateFilter('all');
    setSearchTerm('');
  };

  const formatCurrency = (amount, currency) => {
    const formatter = new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: currency === 'PEN' ? 'PEN' : 'USD',
      minimumFractionDigits: 2
    });
    
    return formatter.format(amount);
  };

  const getMovementTypeIcon = (movement) => {
    // Determinar el tipo de movimiento basado en los campos relacionados
    if (movement.transfer) {
      return (
        <Tooltip title="Transferencia">
          <SwapHorizIcon color="primary" />
        </Tooltip>
      );
    } else if (movement.money_shipment) {
      return (
        <Tooltip title="Envío de Dinero">
          <LocalShippingIcon color="secondary" />
        </Tooltip>
      );
    } else if (movement.amount > 0) {
      return (
        <Tooltip title="Ingreso">
          <ArrowUpwardIcon color="success" />
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title="Egreso">
          <ArrowDownwardIcon color="error" />
        </Tooltip>
      );
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Movimientos de Balance
      </Typography>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              variant="outlined"
              placeholder="Buscar por sucursal, usuario o comentarios..."
              value={searchTerm}
              onChange={handleSearchChange}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={clearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel id="branch-filter-label">Sucursal</InputLabel>
              <Select
                labelId="branch-filter-label"
                value={branchFilter}
                label="Sucursal"
                onChange={(e) => setBranchFilter(e.target.value)}
              >
                <MenuItem value="all">Todas</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.branch_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel id="currency-filter-label">Moneda</InputLabel>
              <Select
                labelId="currency-filter-label"
                value={currencyFilter}
                label="Moneda"
                onChange={(e) => setCurrencyFilter(e.target.value)}
              >
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="PEN">Soles (PEN)</MenuItem>
                <MenuItem value="USD">Dólares (USD)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel id="date-filter-label">Fecha</InputLabel>
              <Select
                labelId="date-filter-label"
                value={dateFilter}
                label="Fecha"
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="today">Hoy</MenuItem>
                <MenuItem value="week">Última semana</MenuItem>
                <MenuItem value="month">Último mes</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={clearFilters}
            >
              Limpiar Filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tipo</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Sucursal</TableCell>
              <TableCell>Monto</TableCell>
              <TableCell>Usuario</TableCell>
              <TableCell>Comentarios</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMovements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No se encontraron movimientos de balance
                </TableCell>
              </TableRow>
            ) : (
              filteredMovements
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((movement) => (
                  <TableRow key={movement.id}>
                    <TableCell>{getMovementTypeIcon(movement)}</TableCell>
                    <TableCell>{new Date(movement.movement_date).toLocaleString()}</TableCell>
                    <TableCell>{movement.branch.branch_name}</TableCell>
                    <TableCell>
                      <Box 
                        sx={{ 
                          color: movement.amount >= 0 ? 'success.main' : 'error.main',
                          fontWeight: 'bold'
                        }}
                      >
                        {formatCurrency(movement.amount, movement.currency_type)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {movement.user ? `${movement.user.first_name} ${movement.user.father_last_name}` : '-'}
                    </TableCell>
                    <TableCell>{movement.comments || '-'}</TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredMovements.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>
    </Box>
  );
};

export default BalanceMovementList; 